"use client";
import React, { ComponentProps, useMemo, useState } from "react";
import { Combobox } from "@headlessui/react";
import InputShell from "./InputShell";
import { HiChevronDown } from "react-icons/hi";
import clsx from "clsx";
import { CgSpinner } from "react-icons/cg";
import { cn } from "@/utils/cn";

const MAX_OPTIONS_LENGTH = 200; // limit number of option items displayed

type Props = {
  onChange: (newValue: string) => void;
  onSelect?: (option: { label: string; value: string }) => void;
  value: string;
  options: Array<{ label: string; value: string }>;
  placeholder?: string;
  isLoading?: boolean;
  name?: string;
  className?: string;
  disabled?: boolean;
  disabledOptions?: string[];
  /** a plain dropdown: the field cannot be typed into, only picked from */
  searchable?: boolean;
} & Omit<ComponentProps<typeof InputShell>, "children">;

function Select({
  label,
  onChange,
  onSelect,
  error,
  value,
  options,
  placeholder = "- Select -",
  isLoading,
  isOptional,
  name,
  className,
  disabled = false,
  disabledOptions,
  searchable = true,
  ...rest
}: Props) {
  const [query, setQuery] = useState("");
  const filteredOptions = useMemo(
    () =>
      query === "" || !searchable
        ? options
        : options.filter((option) => {
            return option.label.toLowerCase().includes(query.toLowerCase());
          }),
    [query, options, searchable]
  );
  const limitedOptions = filteredOptions.filter((x, i) => i < MAX_OPTIONS_LENGTH);
  const inputShell = {
    label,
    error,
    ...(isOptional
      ? { isOptional, optionalLabel: rest.optionalLabel }
      : { required: rest.required })
  };

  return (
    <InputShell {...inputShell}>
      {/* <select
        className="border border-outline w-full min-h-[2.5rem] focus:outline-none px-3 rounded-[0.25rem] text-sm leading-4"
        title={label}
        onChange={(e) => onChange(e.target.value)}
        value={value}
        autoComplete="off"
      >
        <option value="">{isLoading ? '- Loading -' : placeholder}</option>
        {options.map((item) => (
          <option key={item.value} value={item.value}>{item.label}</option>
        ))}
      </select> */}
      <div className="relative">
        <Combobox
          value={value || ""}
          onChange={(newValue) => {
            setQuery("");
            onChange(newValue);
          }}
          disabled={disabled}
        >
          <div className="relative">
            <Combobox.Input
              name={name}
              className={cn(
                "min-h-[2.5rem] w-full rounded-[0.25rem] border border-outline pl-3 pr-10 text-sm leading-4 placeholder:text-[#9BA5B7] focus:outline-none",
                disabled && "bg-slate-100 text-slate-500",
                !searchable && !disabled && "cursor-pointer",
                className
              )}
              type="text"
              readOnly={!searchable}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={placeholder}
              displayValue={(item) => {
                return (
                  options.find((x) => x.value === item)?.label || (isLoading ? "- Loading -" : "")
                );
              }}
            />
            {/* a plain dropdown opens on a click anywhere on the field, the
                chevron alone would leave the rest of it dead */}
            {!searchable && !disabled && (
              <Combobox.Button
                aria-label={label}
                className="absolute inset-0 h-full w-full cursor-pointer"
              />
            )}
            <Combobox.Button
              onClick={() => setQuery("")}
              className="absolute inset-y-0 right-0 flex items-center pr-2"
            >
              {isLoading ? (
                <CgSpinner className="h-5 w-5 animate-spin text-description" />
              ) : (
                <HiChevronDown className="h-5 w-5 text-description" aria-hidden="true" />
              )}
            </Combobox.Button>
          </div>
          <Combobox.Options>
            {filteredOptions.length > 0 && !isLoading ? (
              <div className="absolute right-0 z-20 w-full translate-y-1 rounded border bg-white shadow-modal">
                <div className="max-h-80 overflow-auto">
                  <div className="divide-y">
                    {limitedOptions.map((item) => (
                      <Combobox.Option
                        key={item.value}
                        value={item.value}
                        onClick={() => onSelect?.(item)}
                        disabled={disabledOptions?.some((x) => String(x) === String(item.value))}
                      >
                        {({ active, selected }) => (
                          <div
                            className={clsx(
                              "cursor-pointer px-3 py-2 text-sm",
                              active ? "bg-primary-light" : "",
                              selected ? "font-semibold text-primary-base" : "",
                              disabledOptions?.some((x) => String(x) === String(item.value)) &&
                                "bg-[#EFF1F4] text-[#B0B8C6]"
                            )}
                          >
                            {item.label}
                          </div>
                        )}
                      </Combobox.Option>
                    ))}
                  </div>
                </div>
                {filteredOptions.length >= MAX_OPTIONS_LENGTH ? (
                  <div className="py-1 text-center text-sm text-placeholder">
                    Showing {limitedOptions.length} of {options.length}. Use search
                  </div>
                ) : null}
              </div>
            ) : null}
          </Combobox.Options>
        </Combobox>
      </div>
    </InputShell>
  );
}

export default Select;
